import { Alert } from '@mui/lab'
import { Table, TableBody, TableHead } from '@mui/material'
import cn from 'classnames'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import config from '../../config.json'

import Link from '../common/util/input/Link'
import { get } from '../helpers/Api'
import { formatHoney, formatPercentage } from '../helpers/FormatLinks'
import {
    getBoostAction,
    getClaimableHoneyBalance,
    getHoneyBalance,
    getNFTHiveConfig,
    getNFTHiveDropsConfig,
    getNFTHiveVaultConfig,
    getStakedHoneyBalance,
} from '../helpers/WaxApi'
import Page from '../page/Page'
import { PopupImage } from '../popups/PopupImage'
import { WalletRow } from '../wallet/WalletRow'
import { useSharedState } from '../waxplorer/Waxplorer'

const HoneyView = (props) => {
    const [state, dispatch] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null
    const [honeyBalance, setHoneyBalance] = useState(null)
    const [claimableHoneyBalance, setClaimableHoneyBalance] = useState(null)
    const [rewardedHoney, setRewardedHoney] = useState(null)
    const [stakedHoney, setStakedHoney] = useState(null)
    const [stakedHoneyBalance, setStakedHoneyBalance] = useState(null)
    const [rewardedTokens, setRewardedTokens] = useState(null)
    const [topHoneyEarner, setTopHoneyEarner] = useState(null)

    const [fees, setFees] = useState(0)
    const [shares, setShares] = useState(0)

    const { t } = useTranslation('common')

    const parseHoneyBalance = (res) => {
        if (
            res &&
            res.data &&
            res.data.rows &&
            res.data.rows.length > 0 &&
            res.data.rows[0].balance
        ) {
            setHoneyBalance(
                parseFloat(res.data.rows[0].balance.replace(' HONEY', '')),
            )
        }
    }

    const parseClaimableHoneyBalance = (res) => {
        if (
            res &&
            res.status === 200 &&
            res.data &&
            res.data.rows &&
            res.data.rows.length > 0 &&
            res.data.rows[0].user === userName &&
            res.data.rows[0].rewards
        ) {
            setClaimableHoneyBalance(
                res.data.rows[0].rewards.replace(' HONEY', ''),
            )
        } else {
            setClaimableHoneyBalance(0)
        }
    }

    const parseStakedHoneyBalance = (res) => {
        if (
            res &&
            res.status === 200 &&
            res.data &&
            res.data.rows &&
            res.data.rows.length > 0 &&
            res.data.rows[0].staker === userName &&
            res.data.rows[0].amount
        ) {
            setStakedHoneyBalance(res.data.rows[0].amount.replace(' HONEY', ''))
            const tokens = []
            for (const token of res.data.rows[0]['rewarded_tokens']) {
                const amount = parseFloat(token.split(' ')[0])
                if (amount > 0) {
                    tokens.push(token)
                }
            }
            setRewardedTokens(tokens)
        } else {
            setStakedHoneyBalance(0)
        }
    }

    const parseTotalHoneyRewarded = (res) => {
        if (
            res &&
            res.status === 200 &&
            res.data &&
            res.data.rows &&
            res.data.rows.length === 1 &&
            res.data.rows[0].total_rewarded
        ) {
            setRewardedHoney(
                res.data.rows[0].total_rewarded.replace(' HONEY', ''),
            )
            setStakedHoney(res.data.rows[0].total_staked.replace(' HONEY', ''))
        } else {
            setRewardedHoney(0)
            setStakedHoney(0)
        }
    }

    const parseShares = (res) => {
        if (
            res &&
            res.status === 200 &&
            res.data &&
            res.data.rows &&
            res.data.rows.length === 1 &&
            res.data.rows[0].honey_share
        ) {
            setShares(res.data.rows[0].honey_share)
        } else {
            setShares(0)
        }
    }

    const parseFees = (res) => {
        if (
            res &&
            res.status === 200 &&
            res.data &&
            res.data.rows &&
            res.data.rows.length === 1 &&
            res.data.rows[0].reward_fee
        ) {
            setFees(res.data.rows[0].reward_fee)
        } else {
            setFees(0)
        }
    }

    const claimHoney = async () => {
        return await activeUser.session.transact(
            {
                actions: [
                    getBoostAction(activeUser),
                    {
                        account: 'nfthivevault',
                        name: 'claim',
                        authorization: [
                            {
                                actor: userName,
                                permission: activeUser.requestPermission
                                    ? activeUser.requestPermission
                                    : 'active',
                            },
                        ],
                        data: {
                            claimer: userName,
                        },
                    },
                ],
            },
            {
                expireSeconds: 300,
                blocksBehind: 0,
            },
        )
    }

    const parseTopHoneyEarner = (res) => {
        setTopHoneyEarner(res)
    }

    useEffect(() => {
        if (userName) {
            getHoneyBalance(userName, state).then(parseHoneyBalance)
            getClaimableHoneyBalance(userName, state).then(
                parseClaimableHoneyBalance,
            )
            getStakedHoneyBalance(userName, state).then(parseStakedHoneyBalance)
        }
        getNFTHiveVaultConfig(state).then(parseTotalHoneyRewarded)
        getNFTHiveDropsConfig(state).then(parseFees)
        getNFTHiveConfig(state).then(parseShares)
        get('top-honey-earner/1', 'legacy').then(parseTopHoneyEarner)
    }, [userName])

    const desc = cn('text-lg font-bold  text-center')
    const exchangeTitle = cn('text-center align-top  text-xl text-primary')
    const exchangeDetails = cn('text-center align-top')

    const marketplaceOption = (title, link, description, external) => {
        return (
            <div
                className={cn('pb-3 hover:text-primary-shine hover:font-bold')}
            >
                <Link href={link} external={external}>
                    <div className={exchangeTitle}>{title}</div>
                    <div className={exchangeDetails}>{description}</div>
                </Link>
            </div>
        )
    }

    const claimRewardedTokens = async () => {
        return await activeUser.session.transact(
            {
                actions: [
                    getBoostAction(activeUser),
                    {
                        account: 'nfthivevault',
                        name: 'claimstake',
                        authorization: [
                            {
                                actor: userName,
                                permission: activeUser.requestPermission
                                    ? activeUser.requestPermission
                                    : 'active',
                            },
                        ],
                        data: {
                            claimer: userName,
                        },
                    },
                ],
            },
            {
                expireSeconds: 300,
                blocksBehind: 0,
            },
        )
    }

    const preview = 'https://www.nfthive.io/Hive_HONEY.png'

    const title = config.titles.honey

    const description = config.descriptions.honey

    return (
        <Page
            id="HoneyPage"
            title={title}
            image={preview}
            description={description}
            banners={false}
            {...props}
        >
            <div
                className={'dark:!bg-page bg-zinc-200 w-9/10 rounded-xl m-auto'}
            >
                <div className="md:w-full place-content-center md:h-full p-4">
                    <PopupImage image={'/Logo_HONEY.png'} />
                </div>
                <div className={cn('pb-5 font-normal')}>
                    <div className={cn(desc, 'pb-10 font-normal leading-8')}>
                        HONEY is NFTHive's native token. You make HONEY by
                        selling and buying NFTs on NFTHive.io at rate of 1 HONEY
                        per 100 WAX spent / received. *<br />
                        HONEY can be used to rent CPU. <br />
                        Stake HONEY to earn a share of all fees on Drops (
                        {formatPercentage(fees)}) in{' '}
                        <a
                            href={
                                'https://waxblock.io/account/nfthivedrops?code=nfthivedrops&scope=nfthivedrops&table=rewardconfig&lower_bound=&upper_bound=&limit=10#contract-tables'
                            }
                            target={'_blank'}
                            className={'text-primary'}
                        >
                            all supported Tokens
                        </a>
                        .
                        <br />
                        Stake HONEY to earn a share of{' '}
                        <a
                            href={
                                'https://waxblock.io/account/nft.hive?code=nft.hive&scope=nft.hive&table=honeyconfig&lower_bound=&upper_bound=&limit=10#contract-tables'
                            }
                            target={'_blank'}
                            className={'text-primary'}
                        >
                            {formatPercentage(shares)}
                        </a>{' '}
                        of all secondary market fees.
                        <br />
                        <div className={'opacity-60 pt-2 text-sm'}>
                            * Updated daily
                        </div>
                        <Alert severity="warning">
                            Wash Trading does not grant Rewards!
                        </Alert>
                    </div>
                    <div className={cn('grid grid-cols-3 ')}>
                        <div className={cn(desc, 'pb-10 leading-8')}>
                            <div className={'font-normal'}>
                                Your HONEY Balance:
                            </div>
                            <div className={'font-bold '}>
                                {formatHoney(honeyBalance ? honeyBalance : 0)}
                            </div>
                        </div>
                        <div className={cn(desc, 'pb-10 leading-8')}>
                            <div className={'font-normal'}>
                                Your Staked HONEY:
                            </div>
                            <div className={'font-bold'}>
                                {formatHoney(
                                    stakedHoneyBalance ? stakedHoneyBalance : 0,
                                )}
                            </div>
                            <div className={'font-light'}>
                                {stakedHoneyBalance && stakedHoney
                                    ? '(Share: ' +
                                      formatPercentage(
                                          stakedHoneyBalance / stakedHoney,
                                      ) +
                                      ')'
                                    : ''}
                            </div>
                        </div>
                        <div className={cn(desc, 'pb-10 leading-8')}>
                            <div className={'font-normal'}>
                                Claimable HONEY:
                            </div>
                            <div className={'font-bold'}>
                                {formatHoney(
                                    claimableHoneyBalance
                                        ? claimableHoneyBalance
                                        : 0,
                                )}
                            </div>
                        </div>
                        <div className={cn(desc, 'pb-10 leading-8')}>
                            <div className={'font-normal'}>
                                Total HONEY rewarded (liquid):
                            </div>
                            <div className={'font-bold'}>
                                {formatHoney(rewardedHoney ? rewardedHoney : 0)}
                            </div>
                        </div>
                        <div className={cn(desc, 'pb-10 leading-8')}>
                            <div className={'font-normal'}>
                                Total HONEY staked:
                            </div>
                            <div className={'font-bold'}>
                                {formatHoney(stakedHoney ? stakedHoney : 0)}
                            </div>
                        </div>
                        <div className={cn(desc, 'pb-10 leading-8')}>
                            <div className={'font-normal'}>
                                Total HONEY supply:
                            </div>
                            <div className={'font-bold'}>
                                {formatHoney(10000000)}
                            </div>
                        </div>
                        <div className={cn(desc, 'pb-10 leading-8')}></div>
                        <div className={cn(desc, 'pb-10 leading-8')}>
                            <div className={'font-normal'}>
                                {t('honey.top_honey_earner_24')}:
                            </div>
                            <div className={'font-bold text-primary'}>
                                {topHoneyEarner ? (
                                    <Link
                                        href={
                                            '/user/' + topHoneyEarner['earner']
                                        }
                                    >
                                        {topHoneyEarner['earner']}
                                    </Link>
                                ) : (
                                    ''
                                )}
                            </div>
                            <div className={'font-bold'}>
                                {topHoneyEarner
                                    ? formatHoney(topHoneyEarner['amount'])
                                    : ''}
                            </div>
                        </div>
                        <div className={cn(desc, 'pb-10 leading-8')}></div>
                    </div>
                    <div className={cn('flex flex-wrap justify-evenly')}>
                        {marketplaceOption(
                            'Alcor',
                            'https://wax.alcor.exchange/trade/honey-nfthivehoney_wax-eosio.token',
                            'Trade HONEY on Alcor',
                            true,
                        )}
                        {marketplaceOption(
                            'Taco',
                            'https://swap.tacocrypto.io/swap?output=WAX-eosio.token&input=HONEY-nfthivehoney',
                            'Add liquidity or Swap HONEY on Taco',
                            true,
                        )}
                        {marketplaceOption(
                            'Rent CPU',
                            '/cpu',
                            'Rent CPU with WAX or HONEY',
                            false,
                        )}
                        {marketplaceOption(
                            'NFTHive Drops',
                            '/drops',
                            'Check out the Drops on NFTHive',
                            false,
                        )}
                    </div>
                    <div className={cn('pb-3 hover:text-primary-shine pt-8')}>
                        <div className={exchangeTitle}>{'HONEY Wallet'}</div>
                        <div className={exchangeDetails}>
                            {'Claim HONEY Rewards'}
                        </div>
                    </div>
                    <div className={cn('w-full flex flex-wrap justify-center')}>
                        <Table
                            size="small"
                            sx={{ width: '100%' }}
                            className={
                                'dark:!bg-page bg-zinc-200 p-4 rounded-xl mx-auto overflow-x-auto max-w-240'
                            }
                        >
                            <TableHead></TableHead>
                            <TableBody>
                                <WalletRow
                                    title={`HONEY ${t('navigation.balance')}`}
                                    tokenIcon={'/tokens/honey_nfthivehoney.png'}
                                    content={
                                        honeyBalance
                                            ? formatHoney(honeyBalance)
                                            : 0
                                    }
                                    token={'HONEY'}
                                    balance={honeyBalance}
                                    name={'wallet_honey_balance'}
                                    updateFunc={() => {
                                        dispatch({
                                            type: 'SET_HONEY_BALANCE_CHANGED',
                                            payload: true,
                                        })
                                        getHoneyBalance(userName, state).then(
                                            parseHoneyBalance,
                                        )
                                        getStakedHoneyBalance(
                                            userName,
                                            state,
                                        ).then(parseStakedHoneyBalance)
                                    }}
                                    stakable={true}
                                />
                                <WalletRow
                                    title={`Claimable HONEY`}
                                    tokenIcon={'/tokens/honey_nfthivehoney.png'}
                                    content={formatHoney(
                                        claimableHoneyBalance
                                            ? claimableHoneyBalance
                                            : 0,
                                    )}
                                    hasClaim={true}
                                    balance={claimableHoneyBalance}
                                    name={'wallet_claimable_honey_balance'}
                                    claimFunc={claimHoney}
                                    updateFunc={() => {
                                        dispatch({
                                            type: 'SET_HONEY_BALANCE_CHANGED',
                                            payload: true,
                                        })
                                        getHoneyBalance(userName, state).then(
                                            parseHoneyBalance,
                                        )
                                        getClaimableHoneyBalance(
                                            userName,
                                            state,
                                        ).then(parseClaimableHoneyBalance)
                                        getStakedHoneyBalance(
                                            userName,
                                            state,
                                        ).then(parseStakedHoneyBalance)
                                    }}
                                />
                                <WalletRow
                                    title={`Staked HONEY`}
                                    tokenIcon={'/tokens/honey_nfthivehoney.png'}
                                    content={formatHoney(
                                        stakedHoneyBalance
                                            ? stakedHoneyBalance
                                            : 0,
                                    )}
                                    staked={true}
                                    hasClaim={false}
                                    name={'wallet_staked_honey_balance'}
                                    balance={stakedHoneyBalance}
                                    updateFunc={() => {
                                        dispatch({
                                            type: 'SET_HONEY_BALANCE_CHANGED',
                                            payload: true,
                                        })
                                        getHoneyBalance(userName, state).then(
                                            parseHoneyBalance,
                                        )
                                        getStakedHoneyBalance(
                                            userName,
                                            state,
                                        ).then(parseStakedHoneyBalance)
                                    }}
                                />
                                {rewardedTokens &&
                                    rewardedTokens.length > 0 && (
                                        <WalletRow
                                            title={`Rewarded Tokens`}
                                            content={
                                                <div
                                                    className={
                                                        'flex flex-wrap justify-evenly'
                                                    }
                                                >
                                                    {rewardedTokens.map(
                                                        (token) => (
                                                            <div
                                                                className={
                                                                    'mx-1'
                                                                }
                                                            >
                                                                {token}
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            }
                                            name={'wallet_rewarded_tokens'}
                                            hasClaim={true}
                                            balance={rewardedTokens}
                                            claimFunc={claimRewardedTokens}
                                            updateFunc={() => {
                                                dispatch({
                                                    type: 'SET_HONEY_BALANCE_CHANGED',
                                                    payload: true,
                                                })
                                                getStakedHoneyBalance(
                                                    userName,
                                                    state,
                                                ).then(parseStakedHoneyBalance)
                                            }}
                                        />
                                    )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>
        </Page>
    )
}

export default HoneyView
